import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Boxes, Cpu, Sparkles, Check, X, TrendingDown, TrendingUp, Clock, Zap, Lightbulb, Wind, Sun, ShieldCheck, Network, Wrench, Users } from 'lucide-react';
import { useSiteI18n } from '../SiteI18n';
import { SiteSEO } from '../SiteSEO';
import { SiteCtaBlock } from '../components/SitePageParts';
import { PILLARS } from '../data/pillarsContent';
import { Badge } from '@/components/ui/badge';
import showcaseImg from '../assets/showcase-platform.jpg';

const SitePlatforma: React.FC = () => {
  const { lang } = useSiteI18n();

  return (
    <>
      <SiteSEO
        path="/site/platforma"
        title={lang === 'ro'
          ? 'Platforma AETHERIS — Operating System AI-native pentru organizații'
          : 'AETHERIS Platform — AI-native Operating System for organisations'}
        description={lang === 'ro'
          ? 'O singură platformă, șapte module verticale. Construită pentru scală europeană, conformă GDPR, NIS2 și DSA.'
          : 'One platform, seven vertical modules. Built for European scale, GDPR/NIS2/DSA compliant.'}
      />

      {/* HERO */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-gradient-to-br from-primary/30 via-violet-500/10 to-cyan-500/20 rounded-full blur-[140px] site-aurora" />
          <div className="absolute inset-0 site-grid-bg" />
        </div>

        <div className="container mx-auto px-4 lg:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge className="mb-5 bg-primary/10 text-primary border border-primary/30 backdrop-blur-md">
              <Sparkles className="h-3 w-3 mr-1.5" /> {lang === 'ro' ? 'Platformă' : 'Platform'}
            </Badge>
            <h1 className="text-4xl md:text-7xl font-black tracking-tight leading-[0.98] mb-5">
              <span className="bg-gradient-to-br from-foreground via-foreground to-foreground/40 bg-clip-text text-transparent block">
                {lang === 'ro' ? 'Un Operating System' : 'An Operating System'}
              </span>
              <span className="bg-gradient-to-br from-primary via-primary to-cyan-400 bg-clip-text text-transparent block">
                {lang === 'ro' ? 'pentru organizația ta.' : 'for your organisation.'}
              </span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {lang === 'ro'
                ? 'AETHERIS este construit ca un sistem de operare modular: șapte module verticale, infinite combinații.'
                : 'AETHERIS is built as a modular operating system: seven vertical modules, endless combinations.'}
            </p>
          </motion.div>

          {/* Showcase image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative mt-12 max-w-5xl mx-auto"
          >
            <div className="absolute -inset-8 bg-gradient-to-br from-primary/30 via-cyan-500/20 to-violet-500/20 rounded-[2.5rem] blur-3xl opacity-70" />
            <div className="relative rounded-[1.5rem] overflow-hidden border border-border/60 shadow-2xl shadow-primary/20">
              <img src={showcaseImg} alt="AETHERIS dashboard" className="w-full h-auto" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-tr from-background/30 via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTOR RESULTS — measurable KPIs */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[500px] bg-gradient-to-br from-emerald-500/15 via-primary/15 to-cyan-500/15 rounded-full blur-[140px] site-aurora" />
        </div>
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {lang === 'ro' ? 'Rezultate sectoriale' : 'Sector results'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              {lang === 'ro' ? 'Impact măsurabil, nu promisiuni.' : 'Measurable impact, not promises.'}
            </h2>
            <p className="text-muted-foreground">
              {lang === 'ro'
                ? 'Cifre observate în implementări reale din mediul terțiar și industrial, validate cu benchmark-uri tehnologice de sector.'
                : 'Numbers observed across real deployments in tertiary and industrial environments, validated against sector technology benchmarks.'}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {([
              { icon: TrendingDown, v: '-30%', l: lang === 'ro' ? 'Factură energetică' : 'Energy bills', d: lang === 'ro' ? 'Simulări interne + benchmark de sector' : 'Internal simulations + sector benchmarks', tone: 'from-emerald-500/30 to-emerald-500/5 text-emerald-300' },
              { icon: Wrench, v: '-25%', l: lang === 'ro' ? 'Costuri mentenanță' : 'Maintenance costs', d: lang === 'ro' ? 'Automatizare flux + monitorizare AI' : 'Flow automation + AI monitoring', tone: 'from-cyan-500/30 to-cyan-500/5 text-cyan-300' },
              { icon: TrendingUp, v: '+20%', l: lang === 'ro' ? 'Satisfacție utilizatori' : 'User satisfaction', d: lang === 'ro' ? 'Teste UX + servicii digitale integrate' : 'UX tests + integrated digital services', tone: 'from-violet-500/30 to-violet-500/5 text-violet-300' },
              { icon: Clock, v: '12–18', l: lang === 'ro' ? 'Luni până la ROI' : 'Months to ROI', d: lang === 'ro' ? 'Modele comparative din terțiar și industrie' : 'Comparative tertiary & industrial models', tone: 'from-amber-500/30 to-amber-500/5 text-amber-300' },
            ]).map((k, i) => (
              <motion.div
                key={k.l}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className={`relative rounded-3xl border border-border/60 bg-gradient-to-br ${k.tone} backdrop-blur-md p-6 overflow-hidden hover:border-primary/40 transition-all`}
              >
                <k.icon className="h-6 w-6 mb-3 opacity-90" />
                <div className="text-4xl md:text-5xl font-black tracking-tight bg-gradient-to-br from-foreground to-foreground/60 bg-clip-text text-transparent">
                  {k.v}
                </div>
                <div className="text-sm font-semibold mt-1">{k.l}</div>
                <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{k.d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES PREVIEW */}
      <section className="py-20 bg-card/30 border-y border-border/60">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {lang === 'ro' ? 'Module verticale' : 'Vertical modules'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              {lang === 'ro' ? 'Șapte universuri, un singur sign-on' : 'Seven universes, one sign-on'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {PILLARS.map((p, i) => {
              const c = p[lang];
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  whileHover={{ y: -6 }}
                  className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md hover:border-primary/40 transition-all"
                >
                  <div className="relative h-28 overflow-hidden">
                    <img src={p.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                    <div className={`absolute inset-0 bg-gradient-to-br ${p.gradientClass} mix-blend-overlay`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
                    <div className={`absolute top-2 right-2 h-9 w-9 rounded-xl border bg-card/70 backdrop-blur-md grid place-items-center ${p.accentClass}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{c.tag}</div>
                    <h3 className="font-bold text-sm mb-2">{c.title}</h3>
                    <ul className="space-y-1">
                      {c.modules.slice(0, 4).map((m) => (
                        <li key={m} className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <Check className="h-3 w-3 text-primary shrink-0" /> {m}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={`/site/solutii#${p.slug}`}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    >
                      {lang === 'ro' ? 'Detalii complete' : 'Full details'} <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SMART HUB — capability taxonomy */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {lang === 'ro' ? 'Smart Hub' : 'Smart Hub'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              {lang === 'ro' ? 'Un singur hub. Toate domeniile.' : 'One hub. Every domain.'}
            </h2>
            <p className="text-muted-foreground">
              {lang === 'ro'
                ? 'AETHERIS unifică hardware-ul, software-ul și serviciile pentru cele șase domenii operaționale critice ale unei organizații moderne.'
                : 'AETHERIS unifies hardware, software and services across the six critical operational domains of a modern organisation.'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {([
              { icon: Zap, t: lang === 'ro' ? 'Energie' : 'Energy', items: lang === 'ro'
                ? ['Monitorizare & management', 'Smart meters & submetering', 'Integrare regenerabile + baterii', 'Demand response & load mgmt', 'Stații EV & raportare CO₂']
                : ['Monitoring & management', 'Smart meters & submetering', 'Renewables + battery storage', 'Demand response & load mgmt', 'EV charging & CO₂ reporting'] },
              { icon: Lightbulb, t: lang === 'ro' ? 'Iluminat' : 'Lighting', items: lang === 'ro'
                ? ['Rezidențial & comercial', 'Industrial & urban', 'Smart & connected (KNX/DALI)', 'Iluminat de siguranță', 'Scenarizare & mood control']
                : ['Residential & commercial', 'Industrial & urban', 'Smart & connected (KNX/DALI)', 'Emergency & safety lighting', 'Scene & mood control'] },
              { icon: Wind, t: lang === 'ro' ? 'HVAC & climat' : 'HVAC & climate', items: lang === 'ro'
                ? ['Heating, ventilation, A/C', 'Zone & climate control', 'Mentenanță & monitorizare', 'Smart HVAC integrat', 'Optimizare predictivă AI']
                : ['Heating, ventilation, A/C', 'Zone & climate control', 'Maintenance & monitoring', 'Integrated smart HVAC', 'AI predictive optimisation'] },
              { icon: Sun, t: lang === 'ro' ? 'Calitate aer' : 'Air quality', items: lang === 'ro'
                ? ['Purificare & filtrare', 'Senzori CO₂ / VOC / PM', 'Control umiditate', 'IAQ integrat', 'Alerte & raportare automată']
                : ['Purification & filtration', 'CO₂ / VOC / PM sensors', 'Humidity control', 'Integrated IAQ', 'Auto alerts & reporting'] },
              { icon: ShieldCheck, t: lang === 'ro' ? 'Securitate fizică' : 'Physical security', items: lang === 'ro'
                ? ['Supraveghere video AI', 'Control acces & vizitatori', 'Detecție intruziuni', 'Sisteme alarmă & incendiu', 'Monitorizare 24/7']
                : ['AI video surveillance', 'Access & visitor control', 'Intrusion detection', 'Alarm & fire safety', '24/7 monitoring'] },
              { icon: Network, t: lang === 'ro' ? 'Cybersecurity' : 'Cybersecurity', items: lang === 'ro'
                ? ['Network & endpoint security', 'IAM & Zero Trust', 'SIEM & threat intel', 'Pentest & vulnerability mgmt', 'IoT/OT security & GRC']
                : ['Network & endpoint security', 'IAM & Zero Trust', 'SIEM & threat intelligence', 'Pentest & vulnerability mgmt', 'IoT/OT security & GRC'] },
            ]).map((g, i) => (
              <motion.div
                key={g.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className="group relative rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md p-6 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-primary/15 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity" />
                <div className="relative">
                  <div className="h-11 w-11 rounded-xl bg-primary/15 grid place-items-center text-primary mb-4">
                    <g.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-bold text-base mb-3">{g.t}</h3>
                  <ul className="space-y-1.5">
                    {g.items.map((it) => (
                      <li key={it} className="text-xs text-muted-foreground flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DATA LIFECYCLE */}
      <section className="py-20 bg-card/30 border-y border-border/60">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {lang === 'ro' ? 'Ciclul de viață al unui semnal' : 'Signal lifecycle'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              {lang === 'ro' ? 'De la senzor la decizie. În milisecunde.' : 'From sensor to decision. In milliseconds.'}
            </h2>
            <p className="text-muted-foreground">
              {lang === 'ro'
                ? 'Fiecare semnal — KNX, IoT, video, ticket cetățean — parcurge același traseu auditat, criptat, cu RLS pe fiecare hop.'
                : 'Every signal — KNX, IoT, video, citizen ticket — follows the same audited, encrypted path with RLS on every hop.'}
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-4 max-w-6xl mx-auto">
            {(lang === 'ro' ? [
              { n: '01', t: 'Captare', d: 'Senzor / agent local / cameră / cetățean PWA' },
              { n: '02', t: 'Edge Function', d: 'Validare, normalizare, rate-limit, CSP strict' },
              { n: '03', t: 'PostgreSQL + RLS', d: 'Persistare cu policy {authenticated}, audit log' },
              { n: '04', t: 'AI Orchestrator', d: 'Predicție, clasificare, sugestie sau alertă' },
              { n: '05', t: 'Decizie', d: 'UI realtime + push RFC 8291 + raport oficial' },
            ] : [
              { n: '01', t: 'Capture', d: 'Sensor / local agent / camera / citizen PWA' },
              { n: '02', t: 'Edge function', d: 'Validation, normalisation, rate-limit, strict CSP' },
              { n: '03', t: 'PostgreSQL + RLS', d: 'Persisted with {authenticated} policy, audit log' },
              { n: '04', t: 'AI orchestrator', d: 'Prediction, classification, suggestion or alert' },
              { n: '05', t: 'Decision', d: 'Realtime UI + RFC 8291 push + official report' },
            ]).map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="relative rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md p-5 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all"
              >
                <div className="text-[10px] font-black tracking-[0.2em] text-primary mb-2">{s.n}</div>
                <h3 className="font-bold text-base mb-1.5">{s.t}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.d}</p>
                {i < 4 && (
                  <div className="hidden md:block absolute top-1/2 -right-2.5 h-5 w-5 rounded-full bg-card border border-border/60 grid place-items-center text-primary z-10">
                    <ArrowRight className="h-3 w-3" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY TOOLS FAIL vs AETHERIS */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {lang === 'ro' ? 'Adopție reală' : 'Real adoption'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              {lang === 'ro' ? 'De ce eșuează majoritatea tool-urilor.' : 'Why most tools fail.'}
            </h2>
            <p className="text-muted-foreground">
              {lang === 'ro'
                ? 'Investiții digitale neutilizate, sisteme fragmentate, complexitate excesivă. AETHERIS răspunde la rădăcina problemei.'
                : 'Unused digital investments, fragmented systems, excessive complexity. AETHERIS tackles the root cause.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl border border-destructive/20 bg-destructive/5 backdrop-blur-md p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <X className="h-5 w-5 text-destructive" />
                <h3 className="font-bold text-lg">{lang === 'ro' ? 'Status quo' : 'Status quo'}</h3>
              </div>
              <ul className="space-y-2.5 text-sm text-muted-foreground">
                {(lang === 'ro' ? [
                  'Adopție insuficientă din partea echipelor',
                  'Complexitate excesivă în utilizare zilnică',
                  'Lipsă vizibilitate asupra utilizării reale',
                  'Decuplare față de nevoile de business',
                  'Tool-uri cumpărate, dar nefolosite',
                  'Investiții publice neprofitabile',
                ] : [
                  'Insufficient adoption from teams',
                  'Excessive day-to-day complexity',
                  'No visibility into actual usage',
                  'Misalignment with business needs',
                  'Tools purchased but never used',
                  'Unprofitable public investments',
                ]).map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <X className="h-4 w-4 text-destructive/70 mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl border border-primary/30 bg-primary/5 backdrop-blur-md p-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Check className="h-5 w-5 text-primary" />
                <h3 className="font-bold text-lg">{lang === 'ro' ? 'Răspunsul AETHERIS' : 'The AETHERIS response'}</h3>
              </div>
              <ul className="space-y-2.5 text-sm">
                {(lang === 'ro' ? [
                  'Deployment modular conform priorităților tale',
                  'Interfețe simple, orientate spre teren',
                  'Training și suport personalizat',
                  'Monitorizare integrată a utilizării cu KPI-uri concrete',
                  'Activezi doar funcțiile cu valoare reală',
                  'Îmbunătățire continuă pe baza feedback-ului',
                ] : [
                  'Modular deployment matching your priorities',
                  'Simple, field-oriented interfaces',
                  'Tailored training and ongoing support',
                  'Built-in usage monitoring with concrete KPIs',
                  'Activate only features that deliver real value',
                  'Continuous improvement from your feedback',
                ]).map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          <div className="max-w-3xl mx-auto mt-8 text-center text-sm text-muted-foreground italic">
            {lang === 'ro'
              ? '"Fiecare funcție activată este aliniată unei utilizări reale, observabile. Impactul nu e declarat — e construit zi de zi, împreună."'
              : '"Every activated feature is aligned to real, observable usage. Impact is not declared — it is built day by day, together."'}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-20 bg-card/30 border-y border-border/60">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {lang === 'ro' ? 'AETHERIS vs. status-quo' : 'AETHERIS vs. status quo'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
              {lang === 'ro' ? 'De ce AETHERIS, și nu altceva.' : 'Why AETHERIS, not the rest.'}
            </h2>
          </div>

          <div className="max-w-5xl mx-auto rounded-3xl border border-border/60 bg-card/40 backdrop-blur-md overflow-hidden">
            <div className="grid grid-cols-3 text-sm">
              <div className="p-4 font-bold text-xs uppercase tracking-wider text-muted-foreground border-b border-border/60">
                {lang === 'ro' ? 'Criteriu' : 'Criterion'}
              </div>
              <div className="p-4 font-bold text-xs uppercase tracking-wider text-primary border-b border-border/60 bg-primary/5">
                AETHERIS
              </div>
              <div className="p-4 font-bold text-xs uppercase tracking-wider text-muted-foreground border-b border-border/60">
                {lang === 'ro' ? 'Soluții clasice' : 'Legacy stacks'}
              </div>

              {(lang === 'ro' ? [
                ['Time-to-production', '24h – 90 zile', '6 – 24 luni'],
                ['Stack tehnologic', 'AI-native, modular, open', 'Monolitic, propietar'],
                ['Conformitate UE', 'GDPR · NIS2 · DSA · AI Act by design', 'Patch după patch'],
                ['Vertical coverage', '7 module integrate, un sign-on', 'Vendori multipli, integrări fragile'],
                ['KNX / BMS', 'Agent local + cloud sync nativ', 'Gateway-uri terțe, latență mare'],
                ['Audit & RLS', 'Pe toate tabelele, obligatoriu', 'Opțional, deseori absent'],
              ] : [
                ['Time-to-production', '24h – 90 days', '6 – 24 months'],
                ['Tech stack', 'AI-native, modular, open', 'Monolithic, proprietary'],
                ['EU compliance', 'GDPR · NIS2 · DSA · AI Act by design', 'Patch after patch'],
                ['Vertical coverage', '7 integrated modules, one sign-on', 'Multiple vendors, fragile integrations'],
                ['KNX / BMS', 'Native local agent + cloud sync', 'Third-party gateways, high latency'],
                ['Audit & RLS', 'On every table, mandatory', 'Optional, often absent'],
              ]).map((row, i) => (
                <React.Fragment key={i}>
                  <div className="p-4 text-sm font-semibold border-b border-border/40">{row[0]}</div>
                  <div className="p-4 text-sm bg-primary/5 border-b border-border/40 flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span>{row[1]}</span>
                  </div>
                  <div className="p-4 text-sm text-muted-foreground border-b border-border/40">{row[2]}</div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HUB OF SKILLS */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="outline" className="mb-3 border-primary/40 text-primary bg-primary/5">
              {lang === 'ro' ? 'Hub de competențe' : 'Hub of skills'}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
              {lang === 'ro' ? 'O platformă. O echipă. Un ecosistem.' : 'One platform. One team. One ecosystem.'}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {([
              { icon: Cpu, t: lang === 'ro' ? 'Experți tehnici' : 'Technical experts', d: lang === 'ro' ? 'Specialiști în arhitecturi cloud, edge, AI, KNX, securitate.' : 'Specialists across cloud, edge, AI, KNX, security architectures.' },
              { icon: Network, t: lang === 'ro' ? 'Parteneri tehnologici' : 'Technology partners', d: lang === 'ro' ? 'Ecosistem deschis de parteneri recunoscuți care extind oferta.' : 'Open ecosystem of recognised partners extending the offering.' },
              { icon: Users, t: lang === 'ro' ? 'Dinamică colectivă' : 'Collective dynamics', d: lang === 'ro' ? 'Echipă dedicată succesului proiectului, de la POC la producție.' : 'A team dedicated to your project success, from POC to production.' },
              { icon: Boxes, t: lang === 'ro' ? 'Specialiști de domeniu' : 'Domain specialists', d: lang === 'ro' ? 'Experți de business pe fiecare verticală: clinic, public, industrial.' : 'Business experts per vertical: clinical, public, industrial.' },
            ]).map((s, i) => (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-border/60 bg-card/60 backdrop-blur-md p-6 text-center hover:border-primary/40 transition-all"
              >
                <div className="h-12 w-12 mx-auto rounded-2xl bg-primary/15 text-primary grid place-items-center mb-3">
                  <s.icon className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-base mb-1">{s.t}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <SiteCtaBlock />
    </>
  );
};

export default SitePlatforma;
